/**
 * Backend client — mirrors mobile app's src/api.ts
 * Uses the same endpoints and data shapes.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = BACKEND_URL;

// Phase 22 — pull the auth token from localStorage (set by AuthProvider).
function _authHeader() {
  try {
    const tok = localStorage.getItem('clasifi_token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  } catch {
    return {};
  }
}

async function request(path, init = {}) {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ..._authHeader(), ...(init.headers ?? {}) },
  });
  const raw = await res.text();
  if (!res.ok) {
    // Phase 22 — any 401 on a data endpoint drops the session and bounces
    // the user back to /login. Login endpoint failures carry their own 401
    // and are handled inline (AuthContext.login throws instead of using this
    // helper), so this only fires for stale/missing tokens on protected
    // calls.
    if (res.status === 401 && !path.startsWith('/api/auth/login')) {
      try {
        window.dispatchEvent(new Event('clasifi:401'));
      } catch { /* ignore */ }
    }
    let detail = raw;
    try {
      const p = JSON.parse(raw);
      detail = typeof p.detail === 'string' ? p.detail : (p.detail?.message ?? JSON.stringify(p.detail ?? p));
    } catch {
      // JSON parse failed — keep raw text as detail. Not actionable.
    }
    throw { status: res.status, detail };
  }
  return raw ? JSON.parse(raw) : undefined;
}

// Dashboard
export const getDashboard = () => request('/api/dashboard');

// Stats
export const getStats = () => request('/api/receipts/stats');

// Categories
export const getCategories = () => request('/api/categories');
export const getBuckets = () => request('/api/categories/buckets');
export const checkSimilarCategory = (name) =>
  request('/api/categories/check-similar', { method: 'POST', body: JSON.stringify({ name }) });
export const createCategoryApi = (name, force = false, group = 'Expenses', bucket = null) =>
  request('/api/categories', {
    method: 'POST',
    body: JSON.stringify({ name, force, group, bucket }),
  });
export const renameCategoryApi = (id, name) =>
  request(`/api/categories/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
export const updateCategoryBucketApi = (id, bucket) =>
  request(`/api/categories/${id}`, { method: 'PATCH', body: JSON.stringify({ bucket }) });
export const getCategoryUsage = (id) => request(`/api/categories/${id}/usage`);
export const deleteCategoryApi = (id, reassignTo) =>
  request(`/api/categories/${id}${reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : ''}`, { method: 'DELETE' });
export const getCategorizationStats = () => request('/api/receipts/categorization-stats');
export const getReportingByBucket = (filters = {}) => {
  const p = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, v); });
  const qs = p.toString();
  return request(`/api/reporting/by_bucket${qs ? `?${qs}` : ''}`);
};

// Phase 9 — Smart categorization
export const suggestCategory = (vendor, opts = {}) =>
  request('/api/receipts/suggest-category', {
    method: 'POST',
    body: JSON.stringify({ vendor, ...opts }),
  });
export const suppressRuleSuggestion = (vendor) =>
  request('/api/receipts/suppress-rule-suggestion', {
    method: 'POST',
    body: JSON.stringify({ vendor }),
  });

// Rules
export const listRules = (isActive) => {
  const qs = isActive !== undefined ? `?is_active=${isActive}` : '';
  return request(`/api/rules${qs}`);
};
export const createRule = (vendor_pattern, category_name) =>
  request('/api/rules', { method: 'POST', body: JSON.stringify({ vendor_pattern, category_name }) });
export const updateRule = (id, patch) =>
  request(`/api/rules/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const deleteRule = (id) => request(`/api/rules/${id}`, { method: 'DELETE' });
export const getRulesStats = () => request('/api/rules/stats');

// Phase 10 — Splits + SKU labels
export const setSplitsApi = (id, splits) =>
  request(`/api/receipts/${id}/splits`, { method: 'POST', body: JSON.stringify({ splits }) });
export const clearSplitsApi = (id) =>
  request(`/api/receipts/${id}/splits`, { method: 'DELETE' });
export const taxAllocationPreview = (id, splits) =>
  request(`/api/receipts/${id}/tax-allocation-preview`, { method: 'POST', body: JSON.stringify({ splits }) });
export const extractLineItems = (id) =>
  request(`/api/receipts/${id}/extract-line-items`, { method: 'POST' });
export const listSkuLabels = (q = '') =>
  request(`/api/sku-labels${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const createSkuLabel = (code, label, vendor = null, category = null) =>
  request('/api/sku-labels', { method: 'POST', body: JSON.stringify({ code, label, vendor, category }) });
export const patchSkuLabel = (id, patch) =>
  request(`/api/sku-labels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const deleteSkuLabel = (id) =>
  request(`/api/sku-labels/${id}`, { method: 'DELETE' });

// Receipts CRUD
export function listReceipts(opts = {}) {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.categorization) params.set('categorization', opts.categorization);
  if (opts.sort) params.set('sort', opts.sort);
  if (opts.category) params.set('category', opts.category);
  if (opts.q) params.set('q', opts.q);
  if (opts.start_date) params.set('start_date', opts.start_date);
  if (opts.end_date) params.set('end_date', opts.end_date);
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  const qs = params.toString();
  return request(`/api/receipts${qs ? `?${qs}` : ''}`);
}

export const getReceipt = (id) => request(`/api/receipts/${id}`);

export const patchReceipt = (id, patch) =>
  request(`/api/receipts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

// Phase 17 — Tax discrepancy review
export const postTaxReview = (id, reviewed = true, note = null) =>
  request(`/api/receipts/${id}/tax-review`, {
    method: 'POST',
    body: JSON.stringify({ reviewed, ...(note ? { note } : {}) }),
  });

export const getTaxAccuracy = () => request('/api/tax/accuracy');

// ---- Phase 18 — Cleo + Support ----
export const getCleoThread = () => request('/api/cleo/thread');
export const clearCleoThread = () => request('/api/cleo/thread', { method: 'DELETE' });
export const getSupportConfig = () => request('/api/support/config');
export const submitSupportTicket = (payload) =>
  request('/api/support/ticket', { method: 'POST', body: JSON.stringify(payload) });

export const deleteReceipt = (id) =>
  request(`/api/receipts/${id}`, { method: 'DELETE' });

// Bulk actions
export function bulkAction(ids, action, category) {
  return request('/api/receipts/bulk', {
    method: 'POST',
    body: JSON.stringify({ ids, action, ...(category ? { category } : {}) }),
  });
}

// ---------- Phase 19 — Bulk mutations (ids | filter) ----------
/**
 * All 6 endpoints accept exactly one of `ids` or `filter`. Caller enforces.
 * Filter shape mirrors GET /api/receipts query params.
 */
function _bulk(path, body) {
  return request(`/api/receipts/bulk/${path}`, { method: 'POST', body: JSON.stringify(body) });
}

export const bulkCategorize = (sel, category, category_confirmed) =>
  _bulk('categorize', {
    ...sel,
    category,
    ...(category_confirmed !== undefined ? { category_confirmed } : {}),
  });

export const bulkApprove = (sel, status = 'approved') =>
  _bulk('approve', { ...sel, status });

export const bulkDelete = (sel) => _bulk('delete', sel);

export const bulkMarkDuplicate = (sel, value = true) =>
  _bulk('mark-duplicate', { ...sel, value });

export const bulkClassifyAsset = (sel, potential_asset, asset_type) =>
  _bulk('classify-asset', {
    ...sel,
    potential_asset,
    ...(asset_type !== undefined ? { asset_type } : {}),
  });

export const bulkTaxCode = (sel, tax_rule_key) =>
  _bulk('tax-code', { ...sel, tax_rule_key });

// Phase 20 — tax-type code override (preferred over legacy `tax_rule_key`).
export const bulkTaxCodeV2 = (sel, tax_code) =>
  _bulk('tax-code', { ...sel, tax_code });

// Bookkeeper Mode bulk actions — set `bookkeeper_reviewed` / `tax_review`.
export const bulkMarkReviewed = ({ ids, filter, reviewed = true } = {}) =>
  _bulk('mark-reviewed', { ...(ids ? { ids } : { filter }), reviewed });

export const bulkMarkTaxReviewed = ({ ids, filter, reviewed = true, note } = {}) =>
  _bulk('tax-review', {
    ...(ids ? { ids } : { filter }),
    reviewed,
    ...(note ? { note } : {}),
  });

// Single-receipt tax-code override.
export function patchReceiptTaxCode(id, tax_code) {
  return request(`/api/receipts/${encodeURIComponent(id)}/tax-code`, {
    method: 'PATCH',
    body: JSON.stringify(tax_code),
  });
}

// Fetch province-scoped catalog for the TaxCodePicker.
export function getTaxCodes() {
  return request('/api/tax/codes');
}

// Rule keys — kept in sync with services.tax_engine.ALL_TAX_RULE_KEYS
export const TAX_RULE_KEYS = [
  { key: 'standard', label: 'Standard taxable', hint: 'Full GST/HST/PST for your province' },
  { key: 'basic_groceries', label: 'Basic groceries', hint: 'Zero-rated (federal)' },
  { key: 'prescription_drugs', label: 'Prescription drugs', hint: 'Zero-rated (federal)' },
  { key: 'medical_devices', label: 'Medical devices', hint: 'Zero-rated (federal)' },
  { key: 'exports', label: 'Exports', hint: 'Zero-rated (federal)' },
  { key: 'residential_rent', label: 'Residential rent', hint: 'Exempt (federal)' },
  { key: 'health_services', label: 'Health services', hint: 'Exempt (federal)' },
  { key: 'financial_services', label: 'Financial services', hint: 'Exempt (federal)' },
  { key: 'childcare', label: 'Childcare', hint: 'Exempt (federal)' },
  { key: 'books_qc', label: 'Books (QC)', hint: 'QST-exempt, GST applies' },
  { key: 'childrens_clothing', label: "Children's clothing", hint: 'PST/QST-exempt (BC/SK/MB/QC)' },
  { key: 'restaurant_meals', label: 'Restaurant meals', hint: 'PST-exempt (BC/SK)' },
  { key: 'bicycles', label: 'Bicycles', hint: 'PST-exempt (BC)' },
  { key: 'personal_services', label: 'Personal services', hint: 'RST-exempt (MB)' },
  { key: 'diapers_qc', label: 'Diapers (QC)', hint: 'PST-exempt (QC)' },
];

// Export
export function getExportSummary(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, String(v)); });
  return request(`/api/receipts/export/summary${q.toString() ? `?${q}` : ''}`);
}

export function exportCsvUrl(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, String(v)); });
  return `${BACKEND_URL}/api/receipts/export.csv${q.toString() ? `?${q}` : ''}`;
}

// Images
export const listImages = (id) => request(`/api/receipts/${id}/images`);
export const imageUrl = (id, idx) => `${BACKEND_URL}/api/receipts/${id}/images/${idx}`;

// Phase 26C+ — multipart upload of persisted receipt images.
// Accepts `{ file: File, name?: string }` entries. Backend route is the same
// one the capture + mobile batch-review flows hit:
//   POST /api/receipts/{id}/images  (multipart, field name `files`, up to 4)
export async function uploadReceiptImages(id, items) {
  const form = new FormData();
  for (const it of items) {
    form.append('files', it.file, it.name ?? it.file.name ?? 'image.jpg');
  }
  const res = await fetch(`${BACKEND_URL}/api/receipts/${encodeURIComponent(id)}/images`, {
    method: 'POST',
    headers: _authHeader(), // multipart Content-Type set by the browser
    body: form,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try { detail = await res.text(); } catch { /* noop */ }
    throw { status: res.status, detail };
  }
  return res.json();
}

// Create receipt (manual entry etc.)
export const createReceipt = (payload) =>
  request('/api/receipts', { method: 'POST', body: JSON.stringify(payload) });

// OCR extraction (used by capture + bulk upload)
export const extractReceiptFromImages = (imagesBase64) =>
  request('/api/receipts/extract', {
    method: 'POST',
    body: JSON.stringify({ images: imagesBase64 }),
  });

// Duplicate check
export const checkDuplicate = (vendor, date, total, excludeId) =>
  request('/api/receipts/check-duplicate', {
    method: 'POST',
    body: JSON.stringify({ vendor, date, total, exclude_id: excludeId }),
  });

// ---- Email queue (Phase 6) ----
export function listEmailQueue(status = 'all', limit = 50, offset = 0) {
  const p = new URLSearchParams();
  p.set('status', status); p.set('limit', String(limit)); p.set('offset', String(offset));
  return request(`/api/email/queue?${p}`);
}
export const getEmailQueueStats = () => request('/api/email/queue/stats');
export const retryEmail = (id) => request(`/api/email/queue/${id}/retry`, { method: 'POST' });
export const deleteEmail = (id) => request(`/api/email/queue/${id}`, { method: 'DELETE' });
export const getInboundAddress = () => request('/api/email/inbound-address');

// ---- Phase 11 — Clasifi Score + Bookkeeper Mode ----
export const getScore = () => request('/api/score');

export function listBookkeeperIssues({ priority = 'all', type = 'all', limit = 50, offset = 0 } = {}) {
  const p = new URLSearchParams();
  p.set('priority', priority);
  p.set('type', type);
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  return request(`/api/bookkeeper/issues?${p}`);
}

export const getBookkeeperStats = () => request('/api/bookkeeper/issues/stats');

// ---- Phase 11.5 — Review status + Queue + Progress ----
export const getBookkeeperProgress = () => request('/api/bookkeeper/progress');

export function listBookkeeperQueue({ filter = 'not_reviewed', sort = 'priority_desc', limit = 25, offset = 0 } = {}) {
  const p = new URLSearchParams();
  p.set('filter', filter);
  p.set('sort', sort);
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  return request(`/api/bookkeeper/queue?${p}`);
}

export const acceptReview = (id) =>
  request(`/api/receipts/${id}/accept-review`, { method: 'POST' });

export const unreview = (id) =>
  request(`/api/receipts/${id}/unreview`, { method: 'POST' });

export const bulkAcceptReview = (ids) =>
  request('/api/receipts/bulk/accept-review', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });

// ---- Phase 12 — Tax settings + Tax Ready ----
export const getTaxSettings = () => request('/api/settings/tax');

export const patchTaxSettings = (patch) =>
  request('/api/settings/tax', { method: 'PATCH', body: JSON.stringify(patch) });

export const getTaxTimeline = () => request('/api/tax/timeline');

export const getTaxReadyStatus = () => request('/api/tax/ready-status');

// ---- Phase 13 — Reporting ----
function _reportingParams(f = {}) {
  const p = new URLSearchParams();
  if (f.status) p.set('status', f.status);
  if (f.start_date) p.set('start_date', f.start_date);
  if (f.end_date) p.set('end_date', f.end_date);
  if (f.vendors?.length) p.set('vendors', f.vendors.join(','));
  if (f.categories?.length) p.set('categories', f.categories.join(','));
  if (f.expense_type) p.set('expense_type', f.expense_type);
  if (f.limit) p.set('limit', String(f.limit));
  return p;
}

export const getReportingInsights = (f = {}) =>
  request(`/api/reporting/insights?${_reportingParams(f)}`);

export const getReport = (type, f = {}) =>
  request(`/api/reporting/report/${type}?${_reportingParams(f)}`);

export function reportExportUrl(type, format, f = {}) {
  return `/api/reporting/report/${type}/export.${format}?${_reportingParams(f)}`;
}

// ---- Phase 22 — Admin stats ----
export const getAdminStats = (userId) => {
  const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
  return request(`/api/admin/stats${qs}`);
};

// ---- Phase 24 — Auth profile ----
export const changePassword = (current_password, new_password) =>
  request('/api/auth/change-password', { method: 'POST', body: JSON.stringify({ current_password, new_password }) });
export const updateProfile = (patch) =>
  request('/api/auth/profile', { method: 'PATCH', body: JSON.stringify(patch) });
export const getMe = () => request('/api/auth/me');
export const createPortalSession = () =>
  request('/api/billing/portal', { method: 'POST' });

// ---- Phase 25 — QuickBooks Online integration ----
export const getQbConnectUrl = () => request('/api/quickbooks/connect-url');
export const listQbConnections = () => request('/api/quickbooks/connections');
export const deleteQbConnection = (id) =>
  request(`/api/quickbooks/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const refreshQbConnection = (id) =>
  request(`/api/quickbooks/connections/${encodeURIComponent(id)}/refresh`, { method: 'POST' });
export const getQbEntities = (id) =>
  request(`/api/quickbooks/connections/${encodeURIComponent(id)}/entities`);
export const getQbMappings = (id) =>
  request(`/api/quickbooks/connections/${encodeURIComponent(id)}/mappings`);
export const updateQbMappings = (id, mappings) =>
  request(`/api/quickbooks/connections/${encodeURIComponent(id)}/mappings`, {
    method: 'PUT',
    body: JSON.stringify(mappings),
  });

// ---- Phase 25.2 — Publish-check + Publish ----
export const publishCheckReceipt = (connectionId, receiptId) =>
  request(
    `/api/quickbooks/connections/${encodeURIComponent(connectionId)}/publish-check/${encodeURIComponent(receiptId)}`,
    { method: 'POST' },
  );
export const publishReceipt = (connectionId, receiptId) =>
  request(
    `/api/quickbooks/connections/${encodeURIComponent(connectionId)}/publish/${encodeURIComponent(receiptId)}`,
    { method: 'POST' },
  );

// ---- Phase 25.3 — Bulk publish + Sync log ----
export const bulkPublishReceipts = (connectionId, body) =>
  request(
    `/api/quickbooks/connections/${encodeURIComponent(connectionId)}/bulk-publish`,
    { method: 'POST', body: JSON.stringify(body || {}) },
  );
export const listSyncLog = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.append(k, String(v)); });
  const q = qs.toString();
  return request(`/api/quickbooks/sync-log${q ? `?${q}` : ''}`);
};
export const retrySyncLogEntry = (entryId) =>
  request(`/api/quickbooks/sync-log/${encodeURIComponent(entryId)}/retry`, { method: 'POST' });

// ---- Phase 26A — Email forward slug + admin user roster ----
export const getEmailForwardAddress = () => request('/api/email/forward-address');
export const listAdminUsers = () => request('/api/admin/users');

// ---- Phase 26C — AI Fixed-Asset suggestion actions ----
export const confirmAssetSuggestion = (receiptId) =>
  request(`/api/receipts/${encodeURIComponent(receiptId)}/asset-suggestion/confirm`, { method: 'POST' });
export const dismissAssetSuggestion = (receiptId) =>
  request(`/api/receipts/${encodeURIComponent(receiptId)}/asset-suggestion/dismiss`, { method: 'POST' });
